import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { branchService } from '@/services/branchService'
import { reportService, AgedReceivablesItem } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    const date = new Date(value)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return value
  }
}

const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const buildAddress = (entity?: Partial<BranchEntity> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}

export default function AgedReceivablesReportPrint(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const agingDate = searchParams.get('agingDate') || ''
  const branchIdParam = searchParams.get('branchId')
  const customerIdParam = searchParams.get('customerId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reportData, setReportData] = useState<AgedReceivablesItem[]>([])
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [totals, setTotals] = useState({
    totalNotDues: 0,
    totalDues: 0,
    total_0_30: 0,
    total_31_60: 0,
    total_61_90: 0,
    total_91_180: 0,
    total_181_360: 0,
    total_360_Plus: 0,
    totalAmount: 0,
  })

  useEffect(() => {
    if (!agingDate || !branchIdParam) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)

        const branchId = Number(branchIdParam)
        const customerId = customerIdParam ? Number(customerIdParam) : undefined

        const reportResponse = await reportService.getAgedReceivablesReport({
          agingDate,
          branchId,
          pageNumber: 1,
          pageSize: 10000, // Large page size to get all data for print
          customerId,
        })

        setReportData(reportResponse.items || [])
        setTotals({
          totalNotDues: reportResponse.totalNotDues || 0,
          totalDues: reportResponse.totalDues || 0,
          total_0_30: reportResponse.total_0_30 || 0,
          total_31_60: reportResponse.total_31_60 || 0,
          total_61_90: reportResponse.total_61_90 || 0,
          total_91_180: reportResponse.total_91_180 || 0,
          total_181_360: reportResponse.total_181_360 || 0,
          total_360_Plus: reportResponse.total_360_Plus || 0,
          totalAmount: reportResponse.totalAmount || 0,
        })

        const branchData = await branchService.getBranchById(branchId).catch(() => null)
        setBranch(branchData)
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Unable to load report'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    loadReport()
  }, [agingDate, branchIdParam, customerIdParam])

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing report for print...</p>
        </div>
      </div>
    )

  if (error || !reportData)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Report not found'}</p>
        <Button onClick={() => navigate('/reports/aged-receivables')}>Back to Report</Button>
      </div>
    )

  return (
    <div className="aged-report-print-root p-6 bg-gray-50 print:bg-white">
      {/* Print styles embedded */}
      <style>{`
        @media print {
          @page { 
            size: A3 landscape; 
            margin: 5mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: auto;
            overflow: visible !important;
            margin: 0;
            padding: 0;
            width: 100%;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important }
          .aged-report-print-root {
            margin: 0 !important;
            padding: 0 !important;
            height: auto;
            width: 100% !important;
          }
          .aged-report-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            padding: 2px !important;
            margin: 0 !important;
            padding-bottom: 15mm !important;
          }
          .top-brand {
            width: 100% !important;
            margin-bottom: 1px !important;
            font-size: 8px !important;
          }
          .top-brand > div {
            font-size: 8px !important;
          }
          .aged-report-main-title {
            font-size: 10px !important;
            margin-top: 2px !important;
          }
          table { 
            page-break-inside: auto;
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            font-size: 7px !important;
          }
          .aged-report-table {
            font-size: 7px !important;
          }
          .aged-report-table th,
          .aged-report-table td {
            padding: 3px 2px !important;
            font-size: 7px !important;
            line-height: 1.2 !important;
          }
          .aged-report-table thead th {
            font-size: 8px !important;
            padding: 4px 2px !important;
          }
          .aged-report-table tbody tr td {
            font-size: 7px !important;
          }
          tr { 
            page-break-inside: avoid;
            page-break-after: auto;
          }
          .aged-report-total-row {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            background: #FEF3C7 !important;
            font-weight: 800 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .aged-report-total-row td {
            background: #FEF3C7 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 7px !important;
          }
          thead { 
            display: table-header-group !important;
            page-break-after: avoid;
          }
          tbody {
            display: table-row-group !important;
          }
          tfoot { 
            display: table-footer-group !important;
            page-break-inside: avoid !important;
          }
        }

        .aged-report-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937; width: 100% }
        .aged-report-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: 0.5px solid #ddd; width: 100% }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px; width: 100% }
        .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
        .aged-report-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; width: 100% }

        /* Date filter */
        .date-filter { display:flex; justify-content:space-between; background:#E5E7EB; padding:8px; margin:8px 0; border-radius:4px }
        .date-item { flex:1; text-align:center }

        /* Aged report table */
        .aged-report-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px; table-layout: fixed !important; }
        .aged-report-table th, .aged-report-table td { border:1px solid #F5F5F5; padding:3px; }
        .aged-report-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
        .aged-report-table tbody tr td { vertical-align:middle; font-size:9px }
        .aged-report-total-row { background:#FEF3C7; font-weight:800 }

        /* View mode - make it compact like print */
        @media screen {
          .aged-report-wrapper { max-width: 100%; padding: 12px }
          .top-brand { margin-bottom: 6px }
          .company-title { font-size: 16px }
          .aged-report-main-title { font-size: 18px; margin-top: 6px }
          .divider-strong { margin: 8px 0 }
          .aged-report-table { font-size: 10px; margin-top: 6px }
          .aged-report-table th, .aged-report-table td { padding: 4px }
          .aged-report-table thead th { font-size: 10px }
          .aged-report-table tbody tr td { font-size: 10px }
          .date-filter { padding: 10px; margin: 10px 0 }
        }

        /* Arabic Right-to-left fixes */
        .rtl { direction: rtl; unicode-bidi: embed }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/reports/aged-receivables')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Report
        </Button>
      </div>

      <div className="aged-report-wrapper">
        {/* Header */}
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>{branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>Address:</strong> {buildAddress(branch) || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Email:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Phone:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>CR No:</strong> {branch?.crNo || '-'}
            </div>
          </div>

          <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <img src={getLogoPath()} alt="Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px] rtl">
            <div><strong>{branch?.nameAr || branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>العنوان:</strong> {branch?.addressAr || buildAddress(branch) || '--'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>البريد الإلكتروني:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>هاتف:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}
            </div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        {/* Report Title */}
        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="aged-report-main-title print:text-base" style={{ fontSize: 18, fontWeight: 700 }}>Aged Receivables</div>
        </div>

        {/* Aging Date Filter */}
        <div className="date-filter print:my-2" style={{ backgroundColor: '#E5E7EB', padding: '8px', margin: '8px 0', borderRadius: '4px' }}>
          <div className="date-item" style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 2 }}>▼AGING DATE</div>
            <div style={{ fontSize: 11 }}>{formatDate(agingDate)}</div>
          </div>
        </div>

        {/* Aged Receivables Table */}
        <table className="aged-report-table">
          <thead>
            <tr>
              <th style={{ width: '8%' }}>INVOICE NO</th>
              <th style={{ width: '12%' }}>CUSTOMER</th>
              <th style={{ width: '8%' }}>DATE</th>
              <th style={{ width: '8%' }}>DUE DATE</th>
              <th style={{ width: '7%', textAlign: 'right' }}>NOT DUES</th>
              <th style={{ width: '7%', textAlign: 'right' }}>INV DUES</th>
              <th style={{ width: '6%', textAlign: 'right' }}>0-30 DAYS</th>
              <th style={{ width: '6%', textAlign: 'right' }}>31-60 DAYS</th>
              <th style={{ width: '6%', textAlign: 'right' }}>61-90 DAYS</th>
              <th style={{ width: '7%', textAlign: 'right' }}>91-180 DAYS</th>
              <th style={{ width: '7%', textAlign: 'right' }}>181-360 DAYS</th>
              <th style={{ width: '7%', textAlign: 'right' }}>360+ DAYS</th>
              <th style={{ width: '11%', textAlign: 'right' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {reportData.length === 0 ? (
              <tr>
                <td colSpan={13} style={{ textAlign: 'center', padding: 16 }}>
                  No aged receivables data available
                </td>
              </tr>
            ) : (
              reportData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.invoiceNo || '-'}</td>
                  <td>{item.customerName || '-'}</td>
                  <td>{formatDate(item.invoiceDate)}</td>
                  <td>{formatDate(item.dueDate)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.notDues)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.dues)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_0_30)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_31_60)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_61_90)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_91_180)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_181_360)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.days_360_Plus)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(item.total)}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            {/* Total Row */}
            <tr className="aged-report-total-row">
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                TOTAL
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.totalNotDues)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.totalDues)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_0_30)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_31_60)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_61_90)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_91_180)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_181_360)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.total_360_Plus)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.totalAmount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

